import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Select, Store } from '@ngxs/store';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';
import { GetAllObjectsLinked } from 'src/app/shared/global-files/global-files.actions';

import YukiDocument from '../../models/yuki/YukiDocument';
import { YukiUpdateInformationComponent } from '../yuki-update-information/yuki-update-information.component';
import {
    FetchAllDocuments,
    FetchFile,
    FetchNextFile,
    FetchPreviousFile,
    RefreshDocumentFromYukiAPI,
    SwitchNextFileToFile,
    SwitchPreviousFileToFile,
    VerifyYukiDocument,
} from '../yuki.actions';
import { YukiState } from '../yuki.state';

@Component({
    selector: 'app-yuki-detail-modal',
    templateUrl: './yuki-detail-modal.component.html',
    styleUrls: ['./yuki-detail-modal.component.scss'],
})
export class YukiDetailModalComponent implements OnInit, OnDestroy, AfterViewInit {
    @Select(YukiState.documentsForCurrentFolder)
    documentsForCurrentFolder$: Observable<YukiDocument[]>;
    documents: YukiDocument[] = null;
    currentIndex = 0;
    @Input() yukiDocument: YukiDocument;
    @Input() folderId: number;
    /** If true, automatically run AI VAT extraction when the update tab is initialized */
    @Input() autoRunAi?: boolean = false;
    @ViewChild('detailTabs') nav?;
    // if true => linking tab will be displayed automatically
    @Input() onLinkTab = false;
    @ViewChild(YukiUpdateInformationComponent) yukiUpdateInformationComponent?: YukiUpdateInformationComponent;
    private subscriptions = new Subscription();
    private refreshedFinancialsByYukiId: Set<string> = new Set<string>();
    /** When true, the documentsForCurrentFolder$ subscription skips re-setting yukiDocument and fetching files */
    private isNavigating = false;

    constructor(
        private store: Store,
        public activeModal: NgbActiveModal,
        private logger: NGXLogger,
        private sanitizer: DomSanitizer,
        private fb: FormBuilder,
        private toastr: ToastrService,
        private changeDetectorRef: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.fetchData();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    ngAfterViewInit() {
        if (this.onLinkTab) this.nav.select(2);

        this.changeDetectorRef.detectChanges();
                // If requested, attempt to auto-run AI on the update-information child
                try {
                    if (this.autoRunAi && this.yukiUpdateInformationComponent && typeof this.yukiUpdateInformationComponent.onAiVatClick === 'function') {
                        // ensure the document tab is shown
                        this.nav.select(1);
                        // slight delay to let child initialize
                        setTimeout(() => {
                            try { this.yukiUpdateInformationComponent.onAiVatClick(); } catch (e) { /* ignore */ }
                        }, 200);
                    }
                } catch (e) {
                    // ignore any errors — non-critical
                }
    }

    previousDocument() {
        if (!this.documents?.length || this.currentIndex < 1) return;
        this.currentIndex--;
        this.yukiDocument = this.documents[this.currentIndex];
        if (!this.yukiDocument) return;

        this.logger.debug('Switching previous file to current file');
        this.store.dispatch(new SwitchPreviousFileToFile());

        // First document => no previous file
        if (this.currentIndex < 1) return;

        this.logger.debug('Fetching next file');
        this.fetchPreviousFile();
    }

    nextDocument() {
        if (!this.documents?.length) return;
        // Last document of folder => close modal
        if (this.currentIndex >= this.documents.length - 1) {
            this.activeModal.dismiss();
            this.toastr.success('You reached the last document for this folder.');
            return;
        }

        this.currentIndex++;
        this.yukiDocument = this.documents[this.currentIndex];
        if (!this.yukiDocument) return;

        this.logger.debug('Switching next file to current file');
        this.store.dispatch(new SwitchNextFileToFile());

        // Last document => no next file
        if (this.currentIndex + 1 >= this.documents.length) return;

        this.logger.debug('Fetching next file');
        this.fetchNextFile();
    }

    switchToLinkTab() {
        this.nav.select(2);
    }

    closeModal() {
        this.store.dispatch(new FetchAllDocuments(this.folderId));
        this.store.dispatch(new GetAllObjectsLinked());
        this.activeModal.dismiss();
    }

    verifyDocument() {
        if (!this.yukiDocument?.yukiId) {
            this.toastr.error('Document id not available for verify.');
            return;
        }
        this.subscriptions.add(
            this.store.dispatch(new VerifyYukiDocument(this.yukiDocument.yukiId)).subscribe({
                next: () => {
                    this.toastr.success('Yuki document successfully verified.');
                },
                error: () => {
                    this.logger.error('Failed to update Yuki document.');
                    this.toastr.error('Oops, something went wrong, Please try again later...');
                },
            }),
        );
    }

    hasNextDocument(): boolean {
        if (!this.documents?.length) return false;
        for (let i = this.currentIndex + 1; i < this.documents.length; i++) {
            if (!this.documents[i].verified) return true;
        }
        return false;
    }

    verifyAndNext() {
        if (!this.yukiDocument?.yukiId) {
            this.toastr.error('Document id not available for verify.');
            return;
        }
        // Prevent the documentsForCurrentFolder$ subscription from overriding during navigation
        this.isNavigating = true;
        this.subscriptions.add(
            this.store.dispatch(new VerifyYukiDocument(this.yukiDocument.yukiId)).subscribe({
                next: () => {
                    this.toastr.success('Yuki document successfully verified.');
                    this.navigateToNext();
                },
                error: () => {
                    this.isNavigating = false;
                    this.logger.error('Failed to update Yuki document.');
                    this.toastr.error('Oops, something went wrong, Please try again later...');
                },
            }),
        );
    }

    private navigateToNext() {
        if (!this.documents?.length) {
            this.isNavigating = false;
            this.activeModal.dismiss();
            return;
        }

        // Find the next non-verified document after current index
        let nextIndex = -1;
        for (let i = this.currentIndex + 1; i < this.documents.length; i++) {
            if (!this.documents[i].verified) {
                nextIndex = i;
                break;
            }
        }

        if (nextIndex < 0) {
            this.isNavigating = false;
            this.activeModal.dismiss();
            this.toastr.success('Alle documenten in deze lijst zijn geverifieerd.');
            return;
        }

        this.currentIndex = nextIndex;
        this.yukiDocument = this.documents[this.currentIndex];
        if (!this.yukiDocument) {
            this.isNavigating = false;
            return;
        }

        // Explicitly fetch the file for the new current document
        const fileId = this.yukiDocument.uuid || this.yukiDocument.yukiId;
        if (fileId) {
            this.store.dispatch(new FetchFile(fileId));
        }

        this.refreshFinancialsIfMissing(this.yukiDocument);

        // Pre-fetch the next-next unverified file
        for (let i = this.currentIndex + 1; i < this.documents.length; i++) {
            if (!this.documents[i].verified) {
                const nextFileId = this.documents[i].uuid || this.documents[i].yukiId;
                if (nextFileId) {
                    this.store.dispatch(new FetchNextFile(nextFileId));
                }
                break;
            }
        }

        this.isNavigating = false;
    }

    private fetchData() {
        this.subscriptions.add(
            this.documentsForCurrentFolder$.subscribe((documents) => {
                this.documents = documents || [];
                if (this.isNavigating || !this.yukiDocument?.yukiId || this.documents.length === 0) {
                    return;
                }

                const foundIndex = this.documents.findIndex((document) => document.yukiId === this.yukiDocument.yukiId);
                if (foundIndex >= 0) {
                    this.currentIndex = foundIndex;
                    this.yukiDocument = this.documents[this.currentIndex];
                } else {
                    this.currentIndex = 0;
                    this.yukiDocument = this.documents[0];
                }

                this.refreshFinancialsIfMissing(this.yukiDocument);

                if (this.yukiDocument?.uuid) {
                    this.logger.debug('Fetching file');
                    this.fetchFile();
                }

                if (this.documents.length > this.currentIndex + 1 && this.documents[this.currentIndex + 1]?.uuid) {
                    this.logger.debug('Fetching next file');
                    this.fetchNextFile();
                }
            }),
        );

        if (this.yukiDocument?.uuid) {
            this.logger.debug('Fetching file');
            this.fetchFile();
        }

        this.refreshFinancialsIfMissing(this.yukiDocument);
    }

    private refreshFinancialsIfMissing(document: YukiDocument): void {
        const yukiId = (document?.yukiId || '').toString();
        if (!yukiId || this.refreshedFinancialsByYukiId.has(yukiId)) {
            return;
        }

        const amount = Number(document?.amount ?? 0);
        const amountExcl = Number(document?.amountExcl ?? 0);
        const vatAmount = Number((document as any)?.vatamount ?? (document as any)?.vatAmount ?? 0);
        const shouldRefresh = amount === 0 && amountExcl === 0 && vatAmount === 0;

        if (!shouldRefresh) {
            return;
        }

        this.refreshedFinancialsByYukiId.add(yukiId);
        this.subscriptions.add(
            this.store.dispatch(new RefreshDocumentFromYukiAPI(yukiId)).subscribe({
                error: () => {
                    this.logger.warn(`Could not refresh Yuki financials for document ${yukiId}`);
                },
            }),
        );
    }

    private fetchFileErrorHandling(error) {
        if (error.status === 500) {
            this.logger.error('Failed fetch file of Yuki document due to request error to the Yuki API.');
            this.toastr.error(
                'Oops something went wrong on the server while fetching file of this Yuki document... Are you sure you placed your Yuki credentials?',
            );
            return;
        }
        if (error.status === 401) {
            this.logger.error('Failed fetch file of Yuki document due to problems with Yuki credentials.');
            this.toastr.error('Oops something went wrong on the server... Are you sure you placed your Yuki credentials?');
            return;
        }
        if (error.status === 404) {
            this.logger.error('Failed fetch file of Yuki document due to not found.');
            this.toastr.error('Oops, something went wrong, Please try again later...');
            return;
        }
        this.logger.error('Failed to update VAT and VAT amount of Yuki document');
        this.toastr.error('Oops, something went wrong, Please try again later...');
    }

    private fetchFile() {
        const fileIdentifier = this.yukiDocument?.uuid || this.yukiDocument?.yukiId;
        if (!fileIdentifier) return;

        this.store.dispatch(new FetchFile(fileIdentifier)).subscribe({
            error: (error) => this.fetchFileErrorHandling(error),
        });
    }

    private fetchNextFile() {
        const fileIdentifier = this.documents?.[this.currentIndex + 1]?.uuid || this.documents?.[this.currentIndex + 1]?.yukiId;
        if (!fileIdentifier) return;

        this.store.dispatch(new FetchNextFile(fileIdentifier)).subscribe({
            error: (error) => this.fetchFileErrorHandling(error),
        });
    }

    private fetchPreviousFile() {
        const fileIdentifier = this.documents?.[this.currentIndex - 1]?.uuid || this.documents?.[this.currentIndex - 1]?.yukiId;
        if (!fileIdentifier) return;

        this.store.dispatch(new FetchPreviousFile(fileIdentifier)).subscribe({
            error: (error) => this.fetchFileErrorHandling(error),
        });
    }
}

export interface YukiDocumentDTO {
    vatPercent: number;
    vatAmount: number;
    amount: number;
    amountExcl: number;
    description: string;
    subject: string;
    contactName: string;
    invoiceNumber: string;
    linkedContactId: string;
    documentDate: string;
}
